import { KeyIcon, SignOutIcon, UserCircleIcon } from '@phosphor-icons/react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';

/**
 * Plan 06-03 UserMenu — wires the dropdown actions to real flows:
 *   "Profile"          → placeholder (TODO Phase 8 user profile page).
 *   "Change Password"  → routes to /settings/change-password.
 *   "Logout"           → AuthContext.logout() (best-effort POST /api/auth/logout +
 *                        local clear) → toast → navigate /login.
 */
export function UserMenu(): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onChangePassword = (): void => {
    void navigate('/settings/change-password');
  };

  const onLogout = async (): Promise<void> => {
    await logout();
    toast.success('You have been logged out.');
    void navigate('/login', { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="User menu" data-testid="user-menu-trigger">
          <UserCircleIcon size={20} weight="duotone" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="font-semibold">
          {user?.fullName ?? 'Account'}
        </DropdownMenuLabel>
        {user?.email !== undefined && (
          <div className="px-2 pb-1.5 text-xs text-muted-foreground" data-testid="user-menu-email">
            {user.email}
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            // Profile lands in Phase 8 — surface a friendly notice for now.
            toast.info('Profile page lands in Phase 8.');
          }}
          data-testid="user-menu-profile"
        >
          <UserCircleIcon size={16} className="mr-2" />
          Profile
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onChangePassword} data-testid="user-menu-change-password">
          <KeyIcon size={16} className="mr-2" />
          Change password
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            void onLogout();
          }}
          data-testid="user-menu-logout"
        >
          <SignOutIcon size={16} className="mr-2" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
